import { config } from '../config/env';
import { getGlobalToken } from './authStorage';

export type ProposalState =
  | 'IDLE'
  | 'LOADING'
  | 'READY'
  | 'NO_KNOWLEDGE'
  | 'NO_MATCHING_EVIDENCE'
  | 'UNKNOWN_EMPTY'
  | 'CONFIRMING'
  | 'RECHECKING'
  | 'CREATED'
  | 'CANCELLED'
  | 'FAILED'
  | 'UNKNOWN';

export type SourceRef = {
  sourceRefId: string;
  sourceType: string;
  uri: string;
  ownerSystem: string;
  sourceId: string;
  version: string;
};

export type Provenance = {
  sourceRefId: string;
  indexedAt?: string | null;
  sourceChecksum?: string;
  contentChecksum?: string;
};

export type TaskProposal = {
  proposalId: string;
  contextId: string;
  snapshotId: string;
  title: string;
  description: string;
  status: 'DRAFT' | 'CONFIRMED' | 'CREATED';
  sourceRefs: SourceRef[];
  provenance?: Provenance[];
  freshness?: { status: 'FRESH' | 'STALE' | 'UNKNOWN'; checkedAt?: string };
  warnings?: Array<{ code: string; message: string }>;
  interpretation?: { evidenceStatement?: string };
};

export type CanonicalTask = {
  taskId: string;
  proposalId: string;
  title: string;
  description: string;
  status: 'CREATED';
  createdAt: string;
};

export type TaskProposalResult =
  | { proposalId: string; status: 'PENDING'; task: null }
  | { proposalId: string; status: 'CREATED'; task: CanonicalTask };

export type AssistantSourceReady = {
  ok: true;
  contractVersion: 'assistant.source-ingestion.v1';
  status: 'READY';
  sourceId: string;
  title: string;
  displayLabel: string;
  collectionId: string;
  sourceType: 'DOCUMENT';
  revision: string;
  snapshotId: string;
  readiness: {
    status: 'READY';
    checksum: string;
  };
};

export type CreateAssistantSourceInput = {
  title: string;
  content: string;
  logicalName?: string;
};

export type TaskProposalDraftResult =
  | { proposal: TaskProposal; task: null; idempotent: boolean; status?: 'PROPOSAL_READY'; message?: string }
  | {
      proposal: null;
      task: null;
      status: 'NO_KNOWLEDGE' | 'NO_MATCHING_EVIDENCE' | 'UNKNOWN_EMPTY';
      code: 'NO_KNOWLEDGE' | 'NO_MATCHING_EVIDENCE' | 'UNKNOWN_EMPTY';
      message: string;
    };

export type EvidenceView = {
  id: string;
  title: string;
  label: string;
  location: string;
  version: string;
  sourceTime: string;
  freshness: string;
};

export class AssistantM1Error extends Error {
  code: string;
  statusCode: number;
  outcomeUnknown: boolean;

  constructor(message: string, options: { code?: string; statusCode?: number; outcomeUnknown?: boolean } = {}) {
    super(message);
    this.name = 'AssistantM1Error';
    this.code = options.code ?? 'ASSISTANT_REQUEST_FAILED';
    this.statusCode = options.statusCode ?? 0;
    this.outcomeUnknown = options.outcomeUnknown === true;
  }
}

function requestId(prefix: string) {
  const suffix = crypto.randomUUID().replace(/-/g, '');
  return `${prefix}-${Date.now()}-${suffix}`;
}

function authUrl(path: string) {
  const base = config.authBaseURL?.replace(/\/$/, '') ?? '';
  return `${base}${path}`;
}

async function jsonRequest<T>(url: string, init: RequestInit, timeoutMs: number, outcomeMayHaveChanged: boolean): Promise<T> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, { ...init, signal: controller.signal });
    let payload: any = null;
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }
    if (!response.ok || payload?.ok === false) {
      throw new AssistantM1Error(payload?.error?.message || payload?.message || `HTTP ${response.status}`, {
        code: payload?.error?.code || 'HTTP_ERROR',
        statusCode: response.status,
      });
    }
    return payload as T;
  } catch (error) {
    if (error instanceof AssistantM1Error) throw error;
    const interrupted = error instanceof DOMException && error.name === 'AbortError';
    throw new AssistantM1Error(
      outcomeMayHaveChanged
        ? 'The request was interrupted and its canonical result is unknown.'
        : 'The Assistant service could not be reached.',
      {
        code: interrupted ? 'REQUEST_TIMEOUT' : 'NETWORK_FAILURE',
        outcomeUnknown: outcomeMayHaveChanged,
      },
    );
  } finally {
    window.clearTimeout(timer);
  }
}

async function assistantToken(): Promise<string> {
  const globalToken = getGlobalToken();
  if (!globalToken) {
    throw new AssistantM1Error('Sign in before opening Friday Assistant.', {
      code: 'GLOBAL_TOKEN_REQUIRED',
      statusCode: 401,
    });
  }
  const response = await jsonRequest<{ accessToken: string }>(
    authUrl('/auth/apps/assistant_api/context'),
    {
      method: 'POST',
      headers: { Authorization: `Bearer ${globalToken}` },
    },
    10000,
    false,
  );
  if (!response.accessToken) {
    throw new AssistantM1Error('Auth did not return an assistant_api App Token.', {
      code: 'ASSISTANT_TOKEN_MISSING',
      statusCode: 502,
    });
  }
  return response.accessToken;
}

export async function createAssistantSource(input: CreateAssistantSourceInput): Promise<AssistantSourceReady> {
  const title = input.title.trim();
  const content = input.content.replace(/\r\n/g, '\n');
  const logicalName = input.logicalName?.trim();
  if (!title || title.length > 200) {
    throw new AssistantM1Error('Give this knowledge a title of 200 characters or fewer.', {
      code: 'INVALID_SOURCE',
    });
  }
  if (!content.trim() || content.length > 200_000) {
    throw new AssistantM1Error('Add knowledge content before submitting.', { code: 'INVALID_SOURCE' });
  }
  if (logicalName !== undefined && (!logicalName || logicalName.length > 200)) {
    throw new AssistantM1Error('Logical name must be 200 characters or fewer.', { code: 'INVALID_SOURCE' });
  }

  const token = await assistantToken();
  const id = requestId('web-source');
  const result = await jsonRequest<AssistantSourceReady>(
    '/assistant-api/api/assistant/sources',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Request-Id': id,
        'X-Correlation-Id': id,
      },
      body: JSON.stringify({
        title,
        content,
        ...(logicalName ? { logicalName } : {}),
      }),
    },
    20_000,
    false,
  );

  if (
    result.ok !== true ||
    result.contractVersion !== 'assistant.source-ingestion.v1' ||
    result.status !== 'READY' ||
    result.readiness?.status !== 'READY' ||
    result.sourceType !== 'DOCUMENT' ||
    !result.sourceId ||
    !result.snapshotId ||
    !result.title
  ) {
    throw new AssistantM1Error('Friday could not confirm that the knowledge source is ready.', {
      code: 'SOURCE_INGESTION_FAILED',
      statusCode: 502,
    });
  }
  return result;
}

export async function createTaskProposal(userIntent: string): Promise<TaskProposalDraftResult> {
  const normalized = userIntent.trim();
  if (!normalized) {
    throw new AssistantM1Error('Describe the task you want Friday to propose.', { code: 'USER_INTENT_REQUIRED' });
  }
  const token = await assistantToken();
  const id = requestId('web-draft');
  const result = await jsonRequest<TaskProposalDraftResult>(
    '/assistant-api/api/assistant/task-proposals/draft',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Request-Id': id,
        'X-Correlation-Id': id,
      },
      body: JSON.stringify({
        requestId: id,
        userIntent: normalized,
        query: normalized,
        freshnessRequirement: 'ALLOW_STALE_WITH_WARNING',
        allowPartial: false,
      }),
    },
    20000,
    true,
  );
  if (result.proposal) {
    return result;
  }
  if (
    result.task === null &&
    ['NO_KNOWLEDGE', 'NO_MATCHING_EVIDENCE', 'UNKNOWN_EMPTY'].includes(result.status) &&
    result.code === result.status &&
    typeof result.message === 'string'
  ) {
    return result;
  }
  throw new AssistantM1Error('Assistant returned an invalid Proposal outcome.', {
    code: 'INVALID_PROPOSAL_RESPONSE',
    statusCode: 502,
  });
}

export async function confirmTaskProposal(proposalId: string): Promise<{
  proposal: TaskProposal;
  task: CanonicalTask;
  idempotent: boolean;
}> {
  if (!/^proposal_[a-f0-9]{64}$/.test(proposalId)) {
    throw new AssistantM1Error('The Proposal identity is invalid.', { code: 'INVALID_PROPOSAL_ID' });
  }
  const token = await assistantToken();
  const id = requestId('web-confirm');
  return jsonRequest(
    `/assistant-api/api/assistant/task-proposals/${encodeURIComponent(proposalId)}/confirm`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        'X-Request-Id': id,
        'X-Correlation-Id': id,
      },
      body: JSON.stringify({
        confirmed: true,
        confirmationRef: `web://confirmations/${proposalId}/${id}`,
      }),
    },
    20000,
    true,
  );
}

export async function getTaskProposalResult(proposalId: string): Promise<TaskProposalResult> {
  if (!/^proposal_[a-f0-9]{64}$/.test(proposalId)) {
    throw new AssistantM1Error('The Proposal identity is invalid.', { code: 'INVALID_PROPOSAL_ID' });
  }
  const token = await assistantToken();
  const result = await jsonRequest<{ ok: true } & TaskProposalResult>(
    `/assistant-api/api/assistant/task-proposals/${encodeURIComponent(proposalId)}/result`,
    {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Request-Id': requestId('web-result'),
      },
    },
    10000,
    false,
  );
  if (result.proposalId !== proposalId || !['PENDING', 'CREATED'].includes(result.status)) {
    throw new AssistantM1Error('Assistant returned an invalid Proposal result.', {
      code: 'INVALID_RESULT_RESPONSE',
      statusCode: 502,
    });
  }
  if ((result.status === 'PENDING' && result.task !== null) || (result.status === 'CREATED' && !result.task?.taskId)) {
    throw new AssistantM1Error('Assistant returned an inconsistent Proposal result.', {
      code: 'INVALID_RESULT_RESPONSE',
      statusCode: 502,
    });
  }
  return result;
}

export function evidenceView(proposal: TaskProposal): EvidenceView[] {
  const provenance = new Map((proposal.provenance ?? []).map((item) => [item.sourceRefId, item]));
  return (proposal.sourceRefs ?? []).map((source) => {
    const trace = provenance.get(source.sourceRefId);
    return {
      id: source.sourceRefId,
      title: source.sourceId || source.uri,
      label: [source.ownerSystem, source.sourceType].filter(Boolean).join(' · '),
      location: source.uri,
      version: source.version,
      sourceTime: trace?.indexedAt ?? '',
      freshness: proposal.freshness?.status ?? 'UNKNOWN',
    };
  });
}
